import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '../../services/auth.service';
import { DynamicCssService } from '../../services/dynamic-css.service';
import { UserService } from '../../services/user.service';
import { ChatService } from '../../services/chat.service';
import { ChatRealtimeService } from '../../services/chat-realtime.service';
import { UserDTO } from '../../models/user-dto';
import { DistributorCommonFilterComponent } from '../../components/shared/distributor-common-filter/distributor-common-filter.component';
import { InstallationParams } from '../../models/installation-params';
import { ChatModalComponent } from '../../components/chat/chat-modal/chat-modal.component';

interface MenuItem {
  label: string;
  route: string;
  roles?: string[];
  icon?: 'gear' | 'config';
}

const ALL_MENU_ITEMS: MenuItem[] = [
  { label: 'Planning', route: '/planner', roles: ['Administrador', 'AdminRenault', 'RedAdmin', 'RedJefeTaller', 'RedOperario'] },
  { label: 'Control Almacén', route: '/warehouse', roles: ['Administrador', 'AdminRenault', 'RedAdmin', 'RedJefeAlmacen'] },
  { label: 'Control Citas', route: '/appointment', roles: ['Administrador', 'AdminRenault', 'RedAdmin', 'RedAsesor'] },
  { label: 'Control de calidad', route: '/quality-control', roles: ['Administrador', 'AdminRenault', 'RedAdmin', 'RedJefeTaller'] },
  { label: 'Buscador de OR', route: '/operations', roles: ['Administrador', 'AdminRenault', 'RedAdmin'] },
  { label: 'Tablero de mando', route: '/dashboard', roles: ['Administrador', 'AdminRenault', 'RedAdmin', 'RedJefeTaller'] },
  { label: 'Configurador', route: '/configurator', icon: 'config', roles: ['Administrador', 'AdminRenault', 'RedAdmin', 'RedJefeTaller'] },
  { label: 'Administrador', route: '/admin', roles: ['Administrador'], icon: 'gear' }
];

@Component({
  selector: 'app-default-layout',
  standalone: true,
  imports: [RouterOutlet, RouterLink, RouterLinkActive, CommonModule, DistributorCommonFilterComponent],
  templateUrl: './default-layout.component.html',
  styleUrl: './default-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DefaultLayoutComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private dynamicCss = inject(DynamicCssService);
  private userService = inject(UserService);
  private cdr = inject(ChangeDetectorRef);
  private modalService = inject(NgbModal);
  private chatService = inject(ChatService);
  private chatRealtimeService = inject(ChatRealtimeService);

  inputFilter: InstallationParams = new InstallationParams();
  userInfo: UserDTO;
  userName = signal('');
  menuItems = signal<MenuItem[]>([]);
  // Derivado del estado único de salas de ChatService (CHAT_REVISION.md B8): se actualiza solo con
  // cualquier recarga (mensaje nuevo, lectura, cambio de instalación...), sin petición propia.
  unreadRoomsCount = computed(() => this.chatService.rooms().filter(r => r.HasUnread).length);

  async ngOnInit(): Promise<void> {
    document.body.style.opacity = '0';
    await this.dynamicCss.loadStylesheetLink('main.css', 'main-css');
    document.body.style.opacity = '1';

    this.userService.initializeUserData();
    this.loadUserData();

    // La conexión de chat se abre aquí, al arrancar la sesión, no al abrir la modal del chat —
    // el badge tiene que estar actualizado aunque el usuario nunca haya abierto el chat.
    this.chatRealtimeService.connect();
    // Carga inicial explícita: tras un logout/login en la misma pestaña (modo JWT) la instalación
    // seleccionada puede no cambiar, y entonces el effect de ChatService no se volvería a disparar.
    this.chatService.notifyRoomsChanged();
  }

  ngOnDestroy(): void {
    // Simétrico a connect()/carga inicial en ngOnInit: el layout se destruye en cualquier logout (todos
    // navegan a /login, fuera de él) — ver ChatRealtimeService.disconnect() y ChatService.clearRooms().
    this.chatRealtimeService.disconnect();
    this.chatService.clearRooms();
  }

  loadUserData() {
    const that = this;
    this.userService.getUserData().subscribe(user => {
      this.userInfo = user;
      if (!user) {
        this.userName.set('');
        this.menuItems.set([]);
        that.cdr.detectChanges();
        return;
      }

      that.inputFilter = new InstallationParams();
      that.inputFilter.Installation = [user.IdInstallation];

      this.userName.set(user.Name);
      this.menuItems.set(
        ALL_MENU_ITEMS.filter(item => !item.roles || item.roles.some(r => user.RolesListString.includes(r)))
      );
      that.cdr.detectChanges();
    });
  }

  getDistributorFilter(event: InstallationParams) {
    let idInstallation = event.Installation?.[0] ?? '';
    this.userService.updateLastInstallation(idInstallation).subscribe(res => {
      // Recargar las salas (y el badge) lo hace ChatService por su cuenta al cambiar este signal.
      this.userService.selectedInstallationId.set(idInstallation);
      this.chatRealtimeService.syncRoomsGroups();
    });
  }

  getDistributorInputFilter(event: InstallationParams) {
    const idInstallation = event.Installation?.[0];
    if (idInstallation) {
      this.userService.selectedInstallationId.set(idInstallation);
    }
  }

  async logout(): Promise<void> {
    await this.authService.logout();
  }

  openChat(): void {
    this.modalService.open(ChatModalComponent, { size: 'xl', centered: true });
  }
}
